import { 
  BookmarkIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid';

export default function ChapterList({ 
  chapters, 
  stories, 
  currentStoryIndex, 
  onSelectStory 
}) {
  // Map story chapter indices to story array indices
  const storyByChapter = {};
  stories.forEach((story, idx) => {
    storyByChapter[story.chapter_index] = idx;
  });
  
  // Get current story's chapter index
  const currentChapterIndex = stories[currentStoryIndex]?.chapter_index;
  
  return (
    <div className="card sticky top-24">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <BookmarkIcon className="w-5 h-5 text-rust" />
          <h2 className="font-display text-lg font-semibold text-ink">
            Chapters
          </h2>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-sepia">{chapters.length} total</span>
          <span className="text-rust font-medium">{stories.length} featured</span>
        </div>
      </div>
      
      {/* Chapter list */}
      <div className="space-y-1 max-h-[calc(100vh-280px)] overflow-y-auto pr-2 -mr-2">
        {chapters.map((chapter) => {
          const isSample = chapter.is_sample;
          const storyIndex = storyByChapter[chapter.index];
          const isActive = chapter.index === currentChapterIndex;
          const hasStory = storyIndex !== undefined;
          
          return (
            <button
              key={chapter.index}
              onClick={() => hasStory && onSelectStory(storyIndex)}
              disabled={!hasStory}
              className={`
                w-full text-left px-3 py-2.5 rounded-lg transition-all duration-200 group
                ${isActive 
                  ? 'bg-rust/10 border-l-3 border-rust pl-2.5' 
                  : hasStory 
                    ? 'hover:bg-parchment/50 cursor-pointer border-l-3 border-transparent' 
                    : 'opacity-40 cursor-default'
                }
              `}
              title={!hasStory ? 'Available in full book' : chapter.date}
            >
              <div className="flex items-center gap-3">
                {/* Chapter number */}
                <span className={`
                  flex-shrink-0 w-6 text-center font-display text-sm
                  ${isActive ? 'text-rust font-semibold' : isSample ? 'text-ink font-medium' : 'text-sepia'}
                `}>
                  {chapter.index + 1}
                </span>
                
                {/* Emoji */}
                {chapter.title_emoji && (
                  <span className="flex-shrink-0 text-base opacity-80 group-hover:opacity-100 transition-opacity">
                    {chapter.title_emoji}
                  </span>
                )}
                
                {/* Title */}
                <h3 className={`
                  flex-1 text-sm truncate
                  ${isActive ? 'text-rust font-medium' : isSample ? 'text-ink' : 'text-sepia'}
                `}>
                  {chapter.title}
                </h3>
                
                {/* Featured indicator */}
                {isSample && !isActive && (
                  <SparklesIcon className="w-3.5 h-3.5 text-rust/50 flex-shrink-0" />
                )}
                
                {/* Active indicator */}
                {isActive && (
                  <BookmarkSolidIcon className="w-4 h-4 text-rust flex-shrink-0" />
                )}
              </div>
            </button>
          );
        })}
      </div>
      
      {/* Legend */}
      <div className="mt-4 pt-3 border-t border-parchment/50">
        <div className="flex items-center justify-center gap-4 text-xs text-sepia">
          <div className="flex items-center gap-1.5">
            <SparklesIcon className="w-3 h-3 text-rust/60" />
            <span>Featured</span>
          </div>
          <span className="text-parchment">·</span>
          <div className="flex items-center gap-1.5">
            <span className="text-sepia/50">Faded</span>
            <span>= Full book</span>
          </div>
        </div>
      </div>
    </div>
  );
}

